import type { StoreProcessTaskType } from '@gozoki/api-types'
import type { ComponentType } from 'react'
import {
  CleanIcon,
  HelpCircleIcon,
  PackageDeliveredIcon,
  Truck24Icon,
} from './icons/dashboard-icons'

export interface ProcessStep {
  title: string
  description?: string
  bullets?: string[]
}

export interface ProcessContent {
  /** Libellé de l'onglet (pill). */
  tabLabel: string
  /** Icône de l'onglet + de la boîte 64px. */
  Icon: ComponentType<{ className?: string }>
  /** Titre du corps, ex. « Process de nettoyage — fermeture boutique ». */
  title: string
  frequence: string
  /** Durée mock : n'est affichée que pour les onglets sans tâche réelle ouverte
   *  (l'onglet du type invoqué affiche `task.durationMinutes` issu du back). */
  duree: string
  responsable: string
  intro: string
  steps: ProcessStep[]
  vigilance: string[]
}

// Onglets à contenu réel de la modale (Figma 10263:2353 / 2537 / 2679). La modale ajoute un 4ᵉ
// onglet placeholder « Process 4 — à confirmer » pour les process pas encore définis par le client.
export const PROCESS_CONTENT_ORDER = ['CLEANING', 'RECEPTION', 'LIVRAISON'] as const

// Onglet placeholder : les types de tâche sans SOP (INVENTORY, CLIENT_RELATION) y retombent.
export const PROCESS_PLACEHOLDER: {
  tabLabel: string
  Icon: ComponentType<{ className?: string }>
} = {
  tabLabel: 'Process 4 — à confirmer',
  Icon: HelpCircleIcon,
}

/**
 * Contenu documentaire (SOP) des process finalisés, affiché dans `ProcessModal`. Registry statique
 * sur le modèle de `contact-directory.ts`, fidèle aux maquettes Figma 10263:2353 (Nettoyage),
 * 10263:2537 (Réception) et 10263:2679 (Livraison).
 *
 * Seuls les 3 process validés y figurent ; les autres types de tâche (INVENTORY, CLIENT_RELATION)
 * retombent sur l'onglet placeholder `PROCESS_PLACEHOLDER`. Les seules données réelles proviennent
 * du back (durée/horaire de la tâche invoquée, cf. `ProcessModal`).
 */
export const PROCESS_DIRECTORY: Partial<Record<StoreProcessTaskType, ProcessContent>> = {
  CLEANING: {
    tabLabel: 'Nettoyage',
    Icon: CleanIcon,
    title: 'Process de nettoyage — fermeture boutique',
    frequence: 'Quotidien — à la fermeture',
    duree: '45 min',
    responsable: 'ADV de fermeture',
    intro:
      "Ce process garantit la conformité HACCP de la boutique. Chaque étape doit être réalisée dans l'ordre et cochée dans la fiche de traçabilité. En cas d'anomalie (matériel manquant, panne), créer un ticket SAV avant de quitter la boutique.",
    steps: [
      {
        title: 'Préparer le matériel',
        description:
          "Récupérer le chariot de nettoyage dans le local technique. Vérifier la présence de l'ensemble du matériel avant de commencer :",
        bullets: [
          'Dégraissant alimentaire agréé (bidon vert) et désinfectant de surface (bidon bleu)',
          'Lavettes microfibres propres — une couleur par zone (rouge = chaud, bleu = froid)',
          'Raclette, seau, gants nitrile jetables',
        ],
      },
      {
        title: 'Zone froide — frigos et vitrines',
        description:
          'Vider entièrement la vitrine réfrigérée. Contrôler les DLC de chaque produit : tout produit à J+1 passe en zone "promo fin de journée", tout produit à DLC dépassée part en casse (à scanner).',
        bullets: [
          'Nettoyer les clayettes au dégraissant, rincer, désinfecter',
          'Contrôler la température affichée : entre 0°C et 4°C — si écart, le noter dans la fiche',
          'Remettre les produits en respectant le FIFO (les DLC courtes devant)',
        ],
      },
      {
        title: 'Zone chaude — fours et plans de travail',
        description:
          'Lancer le cycle de nettoyage automatique du four (programme 3, 25 min). Pendant le cycle :',
        bullets: [
          'Dégraisser les plans de travail et la zone de découpe',
          'Vider et désinfecter le bac à déchets, remplacer le sac',
          "Passer la raclette sur les sols, du fond de la boutique vers l'entrée",
        ],
      },
      {
        title: 'Vérification finale et traçabilité',
        description:
          'Faire le tour de la boutique avec la check-list fermeture sur la tablette. Chaque point coché est horodaté et envoyé au siège.',
        bullets: [
          'Éclairages éteints sauf vitrine, four en veille, portes frigos fermées',
          'Signer la fiche de traçabilité sur la tablette (nom + heure)',
        ],
      },
    ],
    vigilance: [
      'Ne jamais mélanger dégraissant et désinfectant (réaction chimique)',
      "Un produit sans étiquette DLC = casse systématique, pas d'exception",
      'Toute température frigo hors plage doit être signalée au SAV le soir même',
    ],
  },
  RECEPTION: {
    tabLabel: 'Réception',
    Icon: PackageDeliveredIcon,
    title: 'Process de réception — livraison quotidienne',
    frequence: 'Quotidien — 07h00-08h00',
    duree: '20 min',
    responsable: "ADV d'ouverture",
    intro:
      'La réception conditionne la fraîcheur en rayon : tout écart non signalé à la réception est à la charge de la boutique. Le contrôle se fait AVANT le départ du chauffeur.',
    steps: [
      {
        title: 'Contrôler le bon de livraison',
        description:
          "Scanner le QR code du bon avec la tablette : la liste attendue s'affiche. Pointer chaque bac livré.",
        bullets: [
          'Vérifier le nombre de bacs par référence (colonne "Qté")',
          'Tout bac manquant ou en trop : le noter directement sur la tablette avant signature',
        ],
      },
      {
        title: 'Vérifier les températures à réception',
        description: 'Sonder 2 bacs au hasard par famille de produits avec le thermomètre laser :',
        bullets: [
          'Ultra-frais : entre 0°C et 4°C',
          'Surgelés : -18°C minimum',
          'Hors plage = refus du bac : photo + motif sur la tablette, le bac repart avec le chauffeur',
        ],
      },
      {
        title: 'Ranger en chambre froide (FIFO)',
        description:
          'Ranger les bacs dans les 15 minutes suivant la dépose : les nouvelles DLC derrière, les anciennes devant. Ne jamais poser de bac à même le sol.',
      },
      {
        title: 'Valider la réception',
        description:
          'Signer sur la tablette (double signature : ADV + chauffeur). Le stock théorique de la boutique est mis à jour automatiquement.',
      },
    ],
    vigilance: [
      'Un bac refusé sans photo = litige perdu, la photo est obligatoire',
      'La chaîne du froid ne doit jamais être rompue plus de 15 minutes',
    ],
  },
  LIVRAISON: {
    tabLabel: 'Livraison',
    Icon: Truck24Icon,
    title: 'Process de livraison — arrivée du camion',
    frequence: '2 à 3 fois par semaine',
    duree: '15 min',
    responsable: 'ADV présent',
    intro:
      "La tablette vous notifie 30 minutes avant l'arrivée estimée du camion. L'objectif : un déchargement rapide, sans rupture de la chaîne du froid, et une zone client toujours dégagée.",
    steps: [
      {
        title: 'Préparer la zone de dépose',
        description: 'Dès la notification "livraison dans 30 min" :',
        bullets: [
          "Dégager le couloir entre l'entrée de service et la chambre froide",
          'Sortir les bacs vides consignés de la précédente livraison',
          'Libérer 2 niveaux dans la chambre froide',
        ],
      },
      {
        title: 'Accueillir le chauffeur-livreur',
        description:
          "Ouvrir l'entrée de service (jamais l'entrée client). Le chauffeur ne rentre pas en zone de vente. Récupérer le bon de livraison ou scanner le QR code sur le premier bac.",
      },
      {
        title: 'Contrôler les quantités livrées',
        description:
          'Pointer les bacs sur la tablette pendant le déchargement (voir process Réception pour le détail des contrôles température).',
      },
      {
        title: 'Valider la livraison sur la tablette',
        description:
          'Double signature ADV + chauffeur. Remettre les bacs vides consignés au chauffeur — leur nombre est saisi sur la tablette pour le suivi des consignes.',
      },
    ],
    vigilance: [
      'Ne jamais laisser la porte de service ouverte sans surveillance',
      'Si le camion a plus de 45 min de retard, appeler le contact Supply (widget Contacts)',
    ],
  },
}
